import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import {
  SvgCheveronDown,
  SvgCheveronRight,
  SvgCog,
  SvgCreditCard,
  SvgReports,
  SvgStoreFront,
  SvgTag,
  SvgTuning,
  SvgWallet,
} from '@actual-app/components/icons/v1';
import { SvgCalendar3 } from '@actual-app/components/icons/v2';
import { View } from '@actual-app/components/view';

import { useIsTestEnv } from '#hooks/useIsTestEnv';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';
import { useIsNocturneTheme } from '#style/theme';

import { Item } from './Item';
import { SecondaryItem } from './SecondaryItem';

export function PrimaryButtons() {
  const { t } = useTranslation();
  const isNocturne = useIsNocturneTheme();
  const [isOpen, setOpen] = useState(false);
  const onToggle = useCallback(() => setOpen(open => !open), []);
  const location = useLocation();

  const syncServerStatus = useSyncServerStatus();
  const isTestEnv = useIsTestEnv();
  const isUsingServer = syncServerStatus !== 'no-server' || isTestEnv;

  const isActive = [
    '/payees',
    '/bank-sync',
    '/tags',
    '/settings',
    '/tools',
    ...(!isNocturne ? ['/rules'] : []),
  ].some(route => location.pathname.startsWith(route));

  useEffect(() => {
    if (isActive) {
      setOpen(true);
    }
  }, [isActive, location.pathname]);

  if (isNocturne) {
    return (
      <View style={{ flexShrink: 0 }}>
        <Item isNocturne title={t('Budget')} shortcut="1" to="/budget" />
        <Item isNocturne title={t('Reports')} shortcut="2" to="/reports" />
        <Item isNocturne title={t('Schedules')} shortcut="3" to="/schedules" />
        <Item isNocturne title={t('Rules')} shortcut="4" to="/rules" />
        <Item
          isNocturne
          title={t('More')}
          onClick={onToggle}
          forceActive={!isOpen && isActive}
        />
        {isOpen && (
          <>
            <Item isNocturne title={t('Payees')} to="/payees" indent={12} />
            {isUsingServer && (
              <Item
                isNocturne
                title={t('Bank Sync')}
                to="/bank-sync"
                indent={12}
              />
            )}
            <Item isNocturne title={t('Tags')} to="/tags" indent={12} />
            <Item isNocturne title={t('Settings')} to="/settings" indent={12} />
          </>
        )}
      </View>
    );
  }

  return (
    <View style={{ flexShrink: 0 }}>
      <Item title={t('Budget')} Icon={SvgWallet} to="/budget" />
      <Item title={t('Reports')} Icon={SvgReports} to="/reports" />
      <Item title={t('Schedules')} Icon={SvgCalendar3} to="/schedules" />
      <Item
        title={t('More')}
        Icon={isOpen ? SvgCheveronDown : SvgCheveronRight}
        onClick={onToggle}
        style={{ marginBottom: isOpen ? 8 : 0 }}
        forceActive={!isOpen && isActive}
      />
      {isOpen && (
        <>
          <SecondaryItem
            title={t('Payees')}
            Icon={SvgStoreFront}
            to="/payees"
            indent={15}
          />
          <SecondaryItem
            title={t('Rules')}
            Icon={SvgTuning}
            to="/rules"
            indent={15}
          />
          {isUsingServer && (
            <SecondaryItem
              title={t('Bank Sync')}
              Icon={SvgCreditCard}
              to="/bank-sync"
              indent={15}
            />
          )}
          <SecondaryItem
            title={t('Tags')}
            Icon={SvgTag}
            to="/tags"
            indent={15}
          />
          <SecondaryItem
            title={t('Settings')}
            Icon={SvgCog}
            to="/settings"
            indent={15}
          />
        </>
      )}
    </View>
  );
}
